import { FC, useState } from 'react'

import {
  Text,
  IconEyeOpen,
  IconXMark,
  Button,
  GreyGrey30,
  GreyGrey80,
  GreyWhite,
  Primary13Regular,
  Primary13SemiBold,
  ShadowL,
  Flex
} from '@sequencehq/core-components'
import { AnimatePresence, motion } from 'framer-motion'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  currentSidebarModeAtom,
  editingVersionAtom,
  lastSavedPresentationAtom,
  presentationAtom,
  resetPresentationHistoryAtom,
  versionPreviewAtom
} from 'Automations/automationEditor/atoms'
import { ModalRestoreVersion } from 'Automations/automationEditor/components/restoreVersion.modal'
import { formatTimestamp } from 'Automations/helpers'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'

export const VersionPreviewToolbar: FC = () => {
  const versionPreview = useAtomValue(versionPreviewAtom)

  return (
    <AnimatePresence>
      {!!versionPreview && (
        <motion.div
          style={{
            position: 'absolute',
            left: '50%',
            x: '-50%',
            bottom: '36px'
          }}
          initial={{ y: '200%' }}
          animate={{ y: '0%' }}
          exit={{ y: '200%' }}
          transition={{ duration: 0.2 }}
        >
          <Flex
            data-testid="automations.editor.versionPreviewToolbar"
            backgroundColor={GreyWhite}
            border={`${GreyGrey30} 1px solid`}
            borderRadius="12px"
            padding="8px 8px 8px 16px"
            boxShadow={ShadowL}
            gap="16px"
            alignItems="center"
          >
            <Flex gap="10px">
              <IconEyeOpen color={GreyGrey80} />
              <Text {...Primary13Regular} color={GreyGrey80}>
                Viewing version{' '}
                <Text {...Primary13SemiBold} color={GreyGrey80} as="span">
                  {formatTimestamp(versionPreview.createdAt)}
                </Text>
              </Text>
            </Flex>

            <Flex gap="8px">
              <ButtonRestore />
              <ButtonClose />
            </Flex>
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const ButtonRestore: FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false)

  return (
    <>
      <Button
        data-testid="automations.editor.versionPreviewToolbar.restore"
        onClick={() => setShowModal(true)}
      >
        Restore
      </Button>
      {showModal && <ModalRestore onClose={() => setShowModal(false)} />}
    </>
  )
}

const ModalRestore: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [versionPreview, setVersionPreview] = useAtom(versionPreviewAtom)
  const setPresentation = useSetAtom(presentationAtom)
  const setEditingVersion = useSetAtom(editingVersionAtom)
  const setLastSavedPresentation = useSetAtom(lastSavedPresentationAtom)
  const setSidebar = useSetAtom(currentSidebarModeAtom)
  const resetHistory = useSetAtom(resetPresentationHistoryAtom)

  return (
    <ModalRestoreVersion
      onClose={onClose}
      onRestore={() => {
        if (!versionPreview) throw new Error('versionPreview is undefined')
        if (!isAutomationPresentation(versionPreview.presentation)) {
          throw new Error('versionPreview presentation is invalid')
        }

        setLastSavedPresentation(versionPreview.presentation)
        setPresentation(versionPreview.presentation)
        setEditingVersion(versionPreview)
        setVersionPreview(undefined)
        setSidebar(undefined)
        resetHistory()
      }}
    />
  )
}

const ButtonClose: FC = () => {
  const setVersionPreview = useSetAtom(versionPreviewAtom)

  return (
    <Button
      data-testid="automations.editor.versionPreviewToolbar.close"
      variant="ghost"
      icon={<IconXMark color={GreyGrey80} />}
      aria-label="Close"
      onClick={() => setVersionPreview(undefined)}
    />
  )
}
